import { type ReactNode } from "react";
import { Link } from "react-router-dom";

import { renderSensitiveInlineMarkdown } from "@/components/common/Sensitive";
import { cn } from "@/lib/utils";
import {
  coalesceFenceTag,
  isFenceBlockTag,
  renderFenceBlock,
  type KaiRenderCtx,
} from "./kaiFenceBlockRegistry";

export interface KaiMarkdownCtx extends KaiRenderCtx {
  isStreaming?: boolean;
  artifacts?: Record<string, unknown>;
  maskSensitive?: boolean;
}

export interface KaiMarkdownParts {
  /** Prose, tables, and non-chart fences. */
  body: ReactNode;
  /** Chart frames, always rendered after `body` at the bottom of the bubble. */
  charts: ReactNode[];
}

const BOOKING_ID_RE = /\b([A-Z]{2,4}-[A-Z0-9][\w-]{2,30})\b/;

function findFirstEntity(
  text: string,
): { index: number; length: number; path: string; label: string } | null {
  const match = text.match(BOOKING_ID_RE);
  if (!match || match.index === undefined) return null;
  return {
    index: match.index,
    length: match[0].length,
    path: `/bookings/${encodeURIComponent(match[1])}`,
    label: match[0],
  };
}

function KaiLink({ href, children }: { href: string; children: ReactNode }) {
  const isInternal = href.startsWith("/");
  const className =
    "font-medium text-primary underline-offset-2 hover:underline";

  if (isInternal) {
    return (
      <Link to={href} className={className}>
        {children}
      </Link>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
    >
      {children}
    </a>
  );
}

type CellAlign = "left" | "center" | "right";

function splitTableRow(line: string): string[] {
  return line
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((c) => c.trim());
}

export function isTableSeparator(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed.includes("-")) return false;
  const cells = trimmed
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|");
  return cells.length > 0 && cells.every((cell) => /^\s*:?-+:?\s*$/.test(cell));
}

function parseAlignments(separatorLine: string): CellAlign[] {
  return splitTableRow(separatorLine).map((cell) => {
    const trimmed = cell.replace(/\s/g, "");
    if (trimmed.startsWith(":") && trimmed.endsWith(":")) return "center";
    if (trimmed.endsWith(":")) return "right";
    return "left";
  });
}

function parseMarkdownTable(
  tableLines: string[],
  tableKey: number,
  processInline: (text: string) => ReactNode,
): ReactNode {
  if (tableLines.length < 2) {
    return <p key={tableKey}>{tableLines.join("\n")}</p>;
  }

  const headerCells = splitTableRow(tableLines[0]);
  let alignments: CellAlign[] = headerCells.map(() => "left");
  let bodyStartIdx = 1;
  if (tableLines.length >= 2 && isTableSeparator(tableLines[1])) {
    alignments = parseAlignments(tableLines[1]);
    bodyStartIdx = 2;
  }

  const bodyRows = tableLines.slice(bodyStartIdx).map(splitTableRow);
  const colCount = headerCells.length;
  const isNumericCol: boolean[] = Array(colCount).fill(true);

  for (const row of bodyRows) {
    for (let c = 0; c < colCount; c++) {
      const val = (row[c] ?? "").replace(/,/g, "").trim();
      if (val === "" || Number.isNaN(Number(val))) {
        isNumericCol[c] = false;
      }
    }
  }

  return (
    <div
      key={`tw-${tableKey}`}
      className="my-2 overflow-x-auto rounded-xl border border-border/60"
    >
      <table className="w-full min-w-[16rem] border-collapse text-left text-[13px]">
        <thead>
          <tr className="border-b border-border/60 bg-foreground/[0.04]">
            {headerCells.map((cell, ci) => (
              <th
                key={ci}
                className="px-3 py-2 font-semibold text-foreground"
                style={{ textAlign: alignments[ci] || "left" }}
              >
                {processInline(cell)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {bodyRows.map((row, ri) => (
            <tr
              key={ri}
              className="border-b border-border/40 last:border-b-0"
            >
              {headerCells.map((_, ci) => (
                <td
                  key={ci}
                  className={cn(
                    "px-3 py-1.5",
                    isNumericCol[ci] && "tabular-nums",
                  )}
                  style={{
                    textAlign: isNumericCol[ci]
                      ? "right"
                      : alignments[ci] || "left",
                  }}
                >
                  {processInline(row[ci] ?? "")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FencePlaceholder({
  label,
  isStreaming,
}: {
  label: string;
  isStreaming?: boolean;
}) {
  return (
    <div className="glass-panel my-2 rounded-xl px-3 py-2 text-xs text-muted-foreground">
      {isStreaming ? `Building ${label.toLowerCase()}…` : label}
    </div>
  );
}

/**
 * Render Kai markdown: headings, lists, tables, links, inline code, bold,
 * fenced blocks, and booking-id auto-links. Structured fences never leak raw
 * JSON while a turn is still streaming.
 *
 * Chart fences are returned separately so callers can pin them to the bottom
 * of the bubble (below any streaming chrome).
 */
export function renderKaiMarkdown(
  text: string,
  ctx: KaiMarkdownCtx = {},
): KaiMarkdownParts {
  const mask = ctx.maskSensitive ?? false;
  const lines = text.split("\n");
  const elements: ReactNode[] = [];
  const charts: ReactNode[] = [];
  let inCodeBlock = false;
  let codeBlockLang = "";
  let codeBlockLines: string[] = [];
  let key = 0;

  const autoLinkEntities = (
    segment: string,
    keyPrefix: number,
  ): ReactNode[] => {
    const result: ReactNode[] = [];
    let remaining = segment;
    let k = keyPrefix;

    while (remaining.length > 0) {
      const entity = findFirstEntity(remaining);
      if (!entity) {
        result.push(renderSensitiveInlineMarkdown(remaining, mask));
        break;
      }
      if (entity.index > 0) {
        result.push(
          renderSensitiveInlineMarkdown(remaining.slice(0, entity.index), mask),
        );
      }
      result.push(
        <KaiLink key={`entity-${k++}`} href={entity.path}>
          {entity.label}
        </KaiLink>,
      );
      remaining = remaining.slice(entity.index + entity.length);
    }

    return result;
  };

  const processInline = (line: string): ReactNode => {
    const parts: ReactNode[] = [];
    let remaining = line;
    let partKey = 0;

    while (remaining.length > 0) {
      const linkMatch = remaining.match(/\[([^\]]+)\]\(([^)]+)\)/);
      const codeMatch = remaining.match(/`([^`]+)`/);
      const boldMatch = remaining.match(/\*\*([^*]+)\*\*/);

      type MatchType = "link" | "code" | "bold";
      let firstMatch: { match: RegExpMatchArray; type: MatchType } | null = null;

      const candidates: { match: RegExpMatchArray; type: MatchType }[] = [];
      if (linkMatch && linkMatch.index !== undefined) {
        candidates.push({ match: linkMatch, type: "link" });
      }
      if (codeMatch && codeMatch.index !== undefined) {
        candidates.push({ match: codeMatch, type: "code" });
      }
      if (boldMatch && boldMatch.index !== undefined) {
        candidates.push({ match: boldMatch, type: "bold" });
      }

      for (const c of candidates) {
        if (
          !firstMatch ||
          (c.match.index ?? Infinity) < (firstMatch.match.index ?? Infinity)
        ) {
          firstMatch = c;
        }
      }

      if (!firstMatch || firstMatch.match.index === undefined) {
        parts.push(...autoLinkEntities(remaining, partKey));
        break;
      }

      const idx = firstMatch.match.index;
      if (idx > 0) {
        parts.push(...autoLinkEntities(remaining.slice(0, idx), partKey));
        partKey += 10;
      }

      if (firstMatch.type === "link") {
        parts.push(
          <KaiLink key={`link-${partKey++}`} href={firstMatch.match[2]}>
            {firstMatch.match[1]}
          </KaiLink>,
        );
      } else if (firstMatch.type === "code") {
        parts.push(
          <code
            key={`code-${partKey++}`}
            className="rounded-md bg-foreground/8 px-1 py-0.5 font-mono text-[12px]"
          >
            {firstMatch.match[1]}
          </code>,
        );
      } else {
        parts.push(
          <strong key={`bold-${partKey++}`} className="font-semibold text-foreground">
            {renderSensitiveInlineMarkdown(firstMatch.match[1], mask)}
          </strong>,
        );
      }

      remaining = remaining.slice(idx + firstMatch.match[0].length);
    }

    return parts.length === 1 ? parts[0] : <>{parts}</>;
  };

  const pushFencedBlock = (rawTag: string, body: string) => {
    const tag = coalesceFenceTag(rawTag, body);
    const structured = renderFenceBlock(tag, body, ctx, key);
    if (structured !== null) {
      // Charts always sit at the bottom of the bubble, regardless of
      // where Kai placed the fence in the prose.
      if (tag === "chart") charts.push(structured);
      else elements.push(structured);
      key++;
      return;
    }
    elements.push(
      <pre
        key={key++}
        className="my-2 overflow-x-auto rounded-xl bg-foreground/[0.05] p-3 font-mono text-[12px] leading-relaxed"
      >
        <code>{body}</code>
      </pre>,
    );
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (line.startsWith("```")) {
      if (inCodeBlock) {
        pushFencedBlock(codeBlockLang, codeBlockLines.join("\n"));
        codeBlockLines = [];
        codeBlockLang = "";
        inCodeBlock = false;
      } else {
        codeBlockLang = line.slice(3).trim().toLowerCase();
        inCodeBlock = true;
      }
      continue;
    }

    if (inCodeBlock) {
      codeBlockLines.push(line);
      continue;
    }

    if (line.startsWith("### ")) {
      elements.push(
        <h3 key={key++} className="pt-1 text-sm font-semibold text-foreground">
          {processInline(line.slice(4))}
        </h3>,
      );
    } else if (line.startsWith("## ")) {
      elements.push(
        <h2 key={key++} className="pt-1 text-base font-semibold text-foreground">
          {processInline(line.slice(3))}
        </h2>,
      );
    } else if (line.startsWith("# ")) {
      elements.push(
        <h1 key={key++} className="pt-1 text-lg font-semibold text-foreground">
          {processInline(line.slice(2))}
        </h1>,
      );
    } else if (line.startsWith("- ") || line.startsWith("* ")) {
      const listItems: ReactNode[] = [];
      let j = i;
      while (
        j < lines.length &&
        (lines[j].startsWith("- ") || lines[j].startsWith("* "))
      ) {
        listItems.push(
          <li key={j} className="pl-0.5">
            {processInline(lines[j].slice(2))}
          </li>,
        );
        j++;
      }
      elements.push(
        <ul key={key++} className="my-1 list-disc space-y-0.5 pl-5">
          {listItems}
        </ul>,
      );
      i = j - 1;
    } else if (/^\d+\. /.test(line)) {
      const listItems: ReactNode[] = [];
      let j = i;
      while (j < lines.length && /^\d+\. /.test(lines[j])) {
        listItems.push(
          <li key={j} className="pl-0.5">
            {processInline(lines[j].replace(/^\d+\. /, ""))}
          </li>,
        );
        j++;
      }
      elements.push(
        <ol key={key++} className="my-1 list-decimal space-y-0.5 pl-5">
          {listItems}
        </ol>,
      );
      i = j - 1;
    } else if (
      line.includes("|") &&
      i + 1 < lines.length &&
      isTableSeparator(lines[i + 1])
    ) {
      const tableLines: string[] = [];
      let j = i;
      while (
        j < lines.length &&
        lines[j].includes("|") &&
        lines[j].trim() !== ""
      ) {
        tableLines.push(lines[j]);
        j++;
      }
      elements.push(parseMarkdownTable(tableLines, key++, processInline));
      i = j - 1;
    } else if (line.trim() === "") {
      // skip blank lines
    } else if (line.startsWith("> ")) {
      elements.push(
        <blockquote
          key={key++}
          className="border-l-2 border-primary/40 pl-3 text-muted-foreground"
        >
          {processInline(line.slice(2))}
        </blockquote>,
      );
    } else {
      elements.push(
        <p key={key++} className="leading-relaxed">
          {processInline(line)}
        </p>,
      );
    }
  }

  if (inCodeBlock) {
    const trailingBody = codeBlockLines.join("\n");
    if (isFenceBlockTag(coalesceFenceTag(codeBlockLang, trailingBody))) {
      pushFencedBlock(codeBlockLang, trailingBody);
    } else if (codeBlockLang === "json" && ctx.isStreaming) {
      elements.push(
        <FencePlaceholder key={key++} label="Result" isStreaming />,
      );
    } else if (codeBlockLines.length > 0) {
      elements.push(
        <pre
          key={key++}
          className="my-2 overflow-x-auto rounded-xl bg-foreground/[0.05] p-3 font-mono text-[12px] leading-relaxed"
        >
          <code>{codeBlockLines.join("\n")}</code>
        </pre>,
      );
    }
  }

  return {
    body: (
      <div className="kai-md min-w-0 max-w-full space-y-2 text-sm leading-relaxed">
        {elements}
        {ctx.isStreaming && (
          <span className="ml-0.5 inline-block h-3.5 w-0.5 animate-pulse bg-primary align-middle" />
        )}
      </div>
    ),
    charts,
  };
}

/** Markdown body with charts pinned to the bottom of the bubble. */
export function KaiMarkdown({
  text,
  ctx,
  afterBody,
}: {
  text: string;
  ctx?: KaiMarkdownCtx;
  /** Rendered between prose and charts (e.g. a streaming status hint). */
  afterBody?: ReactNode;
}) {
  const { body, charts } = renderKaiMarkdown(text, ctx);
  return (
    <>
      {body}
      {afterBody}
      {charts}
    </>
  );
}
